import { Component, input, output, signal, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SHARED_IMPORTS } from '../../core/shared-imports';
import { DynamicDialogConfig, DialogButton } from './dynamic-dialog.model';

@Component({
  selector: 'nexa-dynamic-dialog',
  imports: [DialogModule, ButtonModule, ...SHARED_IMPORTS],
  templateUrl: './dynamic-dialog.html',
  styleUrl: './dynamic-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicDialog {
  private readonly translate = inject(TranslateService);

  visible = input.required<boolean>();
  config = input<DynamicDialogConfig>({});
  content = input<string>('');

  visibleChange = output<boolean>();
  onClose = output<void>();

  computedConfig = computed(() => {
    const cfg = this.config();
    return {
      header: cfg.header || '',
      headerExtra: cfg.headerExtra || '',
      width: cfg.width || '50rem',
      height: cfg.height,
      modal: cfg.modal ?? true,
      draggable: cfg.draggable ?? false,
      resizable: cfg.resizable ?? false,
      closable: cfg.closable ?? true,
      dismissableMask: cfg.dismissableMask ?? false,
      closeOnEscape: cfg.closeOnEscape ?? true,
      showHeader: cfg.showHeader ?? true,
      showFooter: cfg.showFooter ?? (cfg.footerButtons && cfg.footerButtons.length > 0) ?? true,
      footerButtons: cfg.footerButtons || this.getDefaultFooterButtons(),
      customClass: cfg.customClass || '',
      contentClass: cfg.contentClass || '',
      position: cfg.position || 'center',
      maximizable: cfg.maximizable ?? false,
      preserveWhitespace: cfg.preserveWhitespace ?? true,
    };
  });

  dialogStyle = computed(() => {
    const cfg = this.computedConfig();
    const style: any = {};
    if (cfg.width) style.width = cfg.width;
    if (cfg.height) style.height = cfg.height;
    return style;
  });

  private getDefaultFooterButtons(): DialogButton[] {
    return [
      {
        label: this.translate.instant('COMMON.CANCEL'),
        severity: 'danger',
        action: () => this.handleClose(),
        outlined: true,
      },
    ];
  }

  handleClose(): void {
    this.visibleChange.emit(false);
    this.onClose.emit();
  }

  handleVisibleChange(visible: boolean): void {
    if (!visible) {
      this.handleClose();
    }
  }

  handleButtonClick(button: DialogButton): void {
    if (!button.disabled) {
      button.action();
    }
  }
}
